import { Suspense, useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { Link, NavLink, Outlet, ScrollRestoration, useLocation, useNavigate } from 'react-router';
import { Dialog, Icon, type IconName } from '@ebu/ui';
import { BroadcasterControls } from '../features/broadcaster-switch/BroadcasterControls';
import { freshSearchState } from '../features/catalogue/search-navigation';
import { useGuest } from '../features/guest/guest-state';
import { LocalDataDialogButton } from '../features/guest/LocalDataPanel';
import { BunnyPicture, RocketPicture } from '../features/kids/pictures';
import { WhoIsPlaying } from '../features/kids/WhoIsPlaying';
import { GrownUpsPanel } from '../features/screen-time/GrownUpsPanel';
import {
  RestScreen,
  ScreenTimeChip,
  ScreenTimeProvider,
} from '../features/screen-time/screen-time';
import { I18nProvider, useI18n, type MessageKey } from '../i18n/i18n';
import { NavigationFocusContext } from './page';

const NAV: readonly { to: string; label: MessageKey; icon: IconName }[] = [
  { to: '/', label: 'nav.discover', icon: 'discover' },
  { to: '/watch', label: 'nav.cinema', icon: 'watch' },
  { to: '/play', label: 'nav.lab', icon: 'play' },
  { to: '/create', label: 'nav.workshop', icon: 'create' },
];
const MY_STUFF: readonly { to: string; label: MessageKey }[] = [
  { to: '/projects', label: 'nav.projects' },
  { to: '/skills', label: 'nav.skills' },
];

/**
 * Shell (ADR 0011, ADR 0017): for little ones (3–8) the header keeps only big place icons, the
 * age switch and "For grown-ups"; search, broadcaster and language move into the grown-ups panel.
 * Screen time (chip, rest screen) is provided here, so it counts only on child pages.
 *
 * Catalogue-style shell (ADR 0011): a thin dark utility bar that says the prototype is fictional
 * (with Help and "My data"), then a 72px white header — brand, a large search field, text links to
 * the places (Town, Cinema, Lab, Workshop, My stuff) and the broadcaster/language selectors — and a
 * dark footer. Below 900px the place links move to a fixed bottom bar with icons. The broadcaster's
 * server-provided accent colours the header's bottom edge (local identity on a shared core).
 */
export function Shell() {
  const { locale } = useGuest();
  return (
    <I18nProvider locale={locale}>
      <ScreenTimeProvider>
        <ShellLayout />
      </ScreenTimeProvider>
    </I18nProvider>
  );
}

function ShellLayout() {
  const { t } = useI18n();
  const { broadcaster, activeSample, ageMode, setAgeMode } = useGuest();
  const [grownUps, setGrownUps] = useState(false);
  const [choosingAge, setChoosingAge] = useState(false);
  const location = useLocation();
  // Each navigation gets a new location key; the first page load keeps the browser's focus order.
  const [initialKey] = useState(location.key);
  const navigated = location.key !== initialKey;
  const onIsland = location.pathname === '/';

  const accent: CSSProperties | undefined = broadcaster
    ? ({
        '--color-broadcaster': broadcaster.theme.accent,
        '--color-broadcaster-text': broadcaster.theme.accentText,
      } as CSSProperties)
    : undefined;

  return (
    <div
      className={onIsland ? 'shell shell--island' : 'shell'}
      style={accent}
      data-broadcaster={broadcaster?.id}
      data-age={ageMode ?? 'unset'}
    >
      <a className="skip-link" href="#main">
        {t('shell.skipLink')}
      </a>
      <div className="utility-bar">
        <p className="utility-bar__note">{t('shell.fictionalBar')}</p>
        {activeSample && (
          <Link to="/projects" className="utility-bar__sample" data-testid="sample-badge">
            {t('shell.sampleActive', { profile: t(`tree.profile.${activeSample.profile}`) })}
          </Link>
        )}
        <div className="utility-bar__links">
          <NavLink to="/help" className="utility-bar__link">
            {t('shell.help')}
          </NavLink>
          <LocalDataDialogButton className="utility-bar__link" />
        </div>
      </div>
      <header className="hud">
        <Link to="/" className="brand">
          <span className="brand__mark" aria-hidden="true">
            <Icon name="discover" size={22} />
          </span>
          <span className="brand__name">{t('shell.brand')}</span>
        </Link>
        <HeaderSearch />
        <nav className="tabs" aria-label={t('shell.navLabel')}>
          <ul>
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} end={item.to === '/'} className="tab">
                  <Icon name={item.icon} size={22} />
                  <span>{t(item.label)}</span>
                </NavLink>
              </li>
            ))}
            <li>
              <MyStuffMenu />
            </li>
          </ul>
        </nav>
        <div className="hud__end">
          <ScreenTimeChip />
          {ageMode && (
            <button
              type="button"
              className="age-switch"
              aria-label={t('kids.change')}
              title={t('kids.change')}
              onClick={() => setChoosingAge(true)}
            >
              {ageMode === 'little' ? <BunnyPicture /> : <RocketPicture />}
            </button>
          )}
          <BroadcasterControls />
          <button type="button" className="grown-ups-link" onClick={() => setGrownUps(true)}>
            {t('grown.button')}
          </button>
        </div>
      </header>
      <GrownUpsPanel open={grownUps} onClose={() => setGrownUps(false)} />
      <Dialog open={choosingAge} title={t('kids.change')} onCancel={() => setChoosingAge(false)}>
        <WhoIsPlaying
          showDataNote={false}
          onChoose={(mode) => {
            setAgeMode(mode);
            setChoosingAge(false);
          }}
        />
      </Dialog>
      <RestScreen onGrownUps={() => setGrownUps(true)} />
      <main id="main" className="stage" tabIndex={-1}>
        <NavigationFocusContext.Provider value={navigated}>
          <Suspense
            fallback={
              <p className="loading" role="status">
                {t('shell.loading')}
              </p>
            }
          >
            <Outlet />
          </Suspense>
        </NavigationFocusContext.Provider>
      </main>
      <footer className="site-footer">
        <div className="site-footer__inner">
          <ul className="site-footer__links">
            <li>
              <Link to="/help">{t('shell.helpAndData')}</Link>
            </li>
            <li>
              <Link to="/watch">{t('nav.cinema')}</Link>
            </li>
            <li>
              <Link to="/">{t('nav.discover')}</Link>
            </li>
          </ul>
          <p>{t('shell.disclaimer')}</p>
        </div>
      </footer>
      <ScrollRestoration />
    </div>
  );
}

/**
 * Header search: submitting opens the Cinema's search results for the words (Enter or the
 * keyboard's search key). On the Cinema it shows the words currently searched for.
 */
function HeaderSearch() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const id = useId();
  const current = location.pathname.startsWith('/watch')
    ? (new URLSearchParams(location.search).get('q') ?? '')
    : '';
  return (
    <form
      key={current}
      className="header-search"
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        const words = String(new FormData(event.currentTarget).get('q') ?? '')
          .trim()
          .slice(0, 100);
        void navigate(words ? `/watch/search?q=${encodeURIComponent(words)}` : '/watch/search', {
          state: freshSearchState(),
        });
      }}
    >
      <label htmlFor={id} className="header-search__icon">
        <Icon name="search" size={20} />
        <span className="ui-visually-hidden">{t('filter.search')}</span>
      </label>
      <input
        id={id}
        name="q"
        type="search"
        className="header-search__input"
        defaultValue={current}
        maxLength={100}
        autoComplete="off"
        placeholder={t('shell.searchHint')}
      />
    </form>
  );
}

/** "My stuff": a small disclosure with My Projects and My Skills (both arrive in later phases). */
function MyStuffMenu() {
  const { t } = useI18n();
  const location = useLocation();
  const [openAt, setOpenAt] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();
  // Closes itself on navigation: it is only open for the location where it was opened.
  const open = openAt === location.key;
  const active = MY_STUFF.some((item) => location.pathname === item.to);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpenAt(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpenAt(null);
      root.current?.querySelector('button')?.focus();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="menu" ref={root}>
      <button
        type="button"
        className={active ? 'tab tab--active' : 'tab'}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpenAt(open ? null : location.key)}
      >
        <Icon name="projects" size={22} />
        <span>{t('nav.myStuff')}</span>
      </button>
      <ul className="menu__list" id={listId} hidden={!open}>
        {MY_STUFF.map((item) => (
          <li key={item.to}>
            <NavLink to={item.to} className="menu__item">
              {t(item.label)}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}
